import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Button,
  Grid,
  LinearProgress,
  IconButton,
} from '@mui/material';
import {
  CalendarToday,
  DateRange,
  AccessTime,
  Person,
  Computer,
  Place,
  Groups,
  YouTube,
  PlayCircle,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useReducedMotionPreferred } from '../../../hooks/useReducedMotionPreferred';

const MotionCard = motion(Card);

const TRANSPARENT_PIXEL =
  'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';

/**
 * BatchCard
 *
 * @param {object} batch
 * @param {(videoUrl: string) => void} onPlayVideo
 * @param {(plan) => void} onShowInterest
 * @param {(images: string[], index: number) => void} onOpenLightbox
 * @param {(path: string) => string} getImageUrl
 * @param {(date) => string|null} formatDate
 * @param {(url) => string|null} getYouTubeThumbnail
 * @param {(url) => string|null} getYouTubeVideoId
 */
function BatchCard({
  batch,
  onPlayVideo,
  onShowInterest,
  onOpenLightbox,
  getImageUrl,
  formatDate,
  getYouTubeThumbnail,
  getYouTubeVideoId,
}) {
  const reducedMotion = useReducedMotionPreferred();

  const capacityPct = batch.max_students
    ? ((batch.enrolled_students || 0) / batch.max_students) * 100
    : 0;

  const batchImages = batch.images || [];

  const handleThumbClick = (idx) => {
    if (onOpenLightbox && batchImages.length > 0) {
      onOpenLightbox(batchImages.map(getImageUrl), idx);
    }
  };

  const handleInterest = () => {
    if (batch.plan) {
      onShowInterest(batch.plan);
    } else {
      onShowInterest(null);
    }
  };

  return (
    <MotionCard
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      sx={{
        minWidth: { xs: 300, sm: 340 },
        maxWidth: 340,
        borderRadius: '16px',
        overflow: 'hidden',
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        transition: reducedMotion ? 'none' : 'all 0.3s ease',
        '&:hover': reducedMotion ? {} : { boxShadow: 8 },
      }}
    >
      {/* Video thumbnail */}
      {batch.video_url && getYouTubeThumbnail(batch.video_url) && (
        <Box
          sx={{
            position: 'relative',
            cursor: 'pointer',
            '&:hover .play-overlay': { bgcolor: 'rgba(0,0,0,0.6)' },
            '&:hover .play-icon': reducedMotion ? {} : { transform: 'scale(1.1)' },
          }}
          onClick={() => onPlayVideo(batch.video_url)}
        >
          <Box
            component="img"
            src={getYouTubeThumbnail(batch.video_url)}
            alt={batch.name}
            loading="lazy"
            decoding="async"
            sx={{ width: '100%', height: 180, objectFit: 'cover' }}
          />
          <Box
            className="play-overlay"
            sx={{
              position: 'absolute',
              inset: 0,
              bgcolor: 'rgba(0,0,0,0.4)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: reducedMotion ? 'none' : 'background-color 0.3s',
            }}
          >
            <PlayCircle
              className="play-icon"
              sx={{
                fontSize: 64,
                color: 'common.white',
                transition: reducedMotion ? 'none' : 'transform 0.3s',
              }}
            />
          </Box>
          <Chip
            icon={<YouTube />}
            label="Watch Demo"
            size="small"
            sx={{
              position: 'absolute',
              bottom: 8,
              right: 8,
              bgcolor: 'error.main',
              color: 'common.white',
              fontWeight: 600,
              '& .MuiChip-icon': { color: 'common.white' },
            }}
          />
        </Box>
      )}

      {/* Batch images strip (Phase 3) */}
      {batchImages.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            px: 2,
            pt: batch.video_url && getYouTubeThumbnail(batch.video_url) ? 1 : 2,
          }}
        >
          {batchImages.slice(0, 3).map((imgPath, i) => {
            const isThird = i === 2 && batchImages.length > 3;
            return (
              <Box
                key={i}
                sx={{ position: 'relative', flexShrink: 0 }}
                onClick={() => handleThumbClick(i)}
              >
                <Box
                  component="img"
                  src={getImageUrl(imgPath)}
                  alt={`${batch.name} image ${i + 1}`}
                  onError={(e) => {
                    e.currentTarget.src = TRANSPARENT_PIXEL;
                  }}
                  sx={{
                    width: 64,
                    height: 64,
                    borderRadius: '8px',
                    objectFit: 'cover',
                    cursor: 'pointer',
                    display: 'block',
                  }}
                />
                {isThird && (
                  <Box
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      borderRadius: '8px',
                      bgcolor: 'rgba(0,0,0,0.55)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{ color: 'common.white', fontWeight: 700, fontSize: '0.85rem' }}
                    >
                      +{batchImages.length - 3}
                    </Typography>
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>
      )}

      <CardContent sx={{ p: 2.5, flex: 1, display: 'flex', flexDirection: 'column' }}>
        {/* Title + mode chip */}
        <Box display="flex" justifyContent="space-between" alignItems="start" mb={1.5}>
          <Box flex={1}>
            <Typography variant="h6" fontWeight={700}>
              {batch.name}
            </Typography>
            {batch.course_type && (
              <Typography variant="caption" color="text.secondary">
                {batch.course_type}
              </Typography>
            )}
          </Box>
          <Box display="flex" gap={0.5} flexShrink={0} ml={1}>
            <Chip
              label={batch.mode}
              size="small"
              color={
                batch.mode === 'online'
                  ? 'info'
                  : batch.mode === 'hybrid'
                  ? 'warning'
                  : 'default'
              }
              icon={
                batch.mode === 'online' ? (
                  <Computer fontSize="small" />
                ) : (
                  <Place fontSize="small" />
                )
              }
            />
            {batch.video_url && !getYouTubeThumbnail(batch.video_url) && (
              <IconButton
                size="small"
                color="error"
                onClick={() => onPlayVideo(batch.video_url)}
                aria-label="Play video"
              >
                <YouTube />
              </IconButton>
            )}
          </Box>
        </Box>

        {batch.description && (
          <Typography variant="body2" color="text.secondary" mb={2}>
            {batch.description}
          </Typography>
        )}

        {/* Details grid */}
        <Grid container spacing={1.5} sx={{ mb: 2 }}>
          {batch.start_date && (
            <Grid item xs={6}>
              <Box display="flex" alignItems="center" gap={1}>
                <CalendarToday fontSize="small" color="primary" />
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Starts
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {formatDate(batch.start_date)}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          )}
          {batch.end_date && (
            <Grid item xs={6}>
              <Box display="flex" alignItems="center" gap={1}>
                <DateRange fontSize="small" color="action" />
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Ends
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {formatDate(batch.end_date)}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          )}
          {batch.timings && (
            <Grid item xs={6}>
              <Box display="flex" alignItems="center" gap={1}>
                <AccessTime fontSize="small" color="action" />
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Timings
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {batch.timings}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          )}
          {batch.faculty_name && (
            <Grid item xs={6}>
              <Box display="flex" alignItems="center" gap={1}>
                <Person fontSize="small" color="action" />
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Faculty
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {batch.faculty_name}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          )}
          {/* Phase 3: location row */}
          {batch.location && batch.mode !== 'online' && (
            <Grid item xs={12}>
              <Box display="flex" alignItems="center" gap={1}>
                <Place fontSize="small" color="action" />
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Location
                  </Typography>
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    title={batch.location}
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      maxWidth: 240,
                    }}
                  >
                    {batch.location}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          )}
        </Grid>

        {/* Class days */}
        {batch.days?.length > 0 && (
          <Box mb={2}>
            <Typography variant="caption" color="text.secondary" gutterBottom>
              Class Days
            </Typography>
            <Box display="flex" flexWrap="wrap" gap={0.5} mt={0.5}>
              {batch.days.map((day, idx) => (
                <Chip key={idx} label={day.slice(0, 3)} size="small" variant="outlined" />
              ))}
            </Box>
          </Box>
        )}

        {/* Capacity + deadline + price row */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Box display="flex" gap={2}>
            {batch.max_students && (
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Capacity
                </Typography>
                <Box display="flex" alignItems="center" gap={0.5}>
                  <Typography variant="body2" fontWeight={600}>
                    {batch.enrolled_students || 0}/{batch.max_students}
                  </Typography>
                  <Groups fontSize="small" color="action" />
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={capacityPct}
                  color={capacityPct >= 90 ? 'error' : 'primary'}
                  sx={{ width: 60, height: 4, borderRadius: '2px', mt: 0.5 }}
                />
              </Box>
            )}
            {batch.registration_deadline && (
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Register By
                </Typography>
                <Typography variant="body2" fontWeight={600} color="error.main">
                  {formatDate(batch.registration_deadline)}
                </Typography>
              </Box>
            )}
          </Box>
          {batch.price_cents > 0 && (
            <Chip
              label={`₹ ${batch.price_cents.toLocaleString()}`}
              color="primary"
              sx={{ fontWeight: 700 }}
            />
          )}
        </Box>

        {/* Show Interest CTA */}
        <Button
          fullWidth
          variant="contained"
          onClick={handleInterest}
          sx={{
            borderRadius: '12px',
            py: 1.2,
            fontWeight: 700,
            mt: 'auto',
          }}
        >
          Show Interest
        </Button>
      </CardContent>
    </MotionCard>
  );
}

export default BatchCard;
